import { inject } from "ui-common/di";
import type { IRpcService } from "ui-common/services/rpc/types";
import type {
  CreateCreatorMessageResult,
  UpdateCreatorMessageResult,
  GetCreatorMessageByIdResult,
  GetMessageSegmentsResult,
} from "api-contracts/rpc/creator-message";
import type { SendCreatorMessageResult } from "api-contracts/rpc/send-creator-message";
import type {
  IWritableCreatorMessageFormState,
  ICreatorMessageFormService,
  CreatorMessageFormData,
  CreatorMessageFormErrors,
} from "./types";

export class CreatorMessageFormService implements ICreatorMessageFormService {
  @inject<IWritableCreatorMessageFormState>("writableCreatorMessageFormState")
  private state!: IWritableCreatorMessageFormState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  private translateFn: (key: string) => string = (key) => key;
  private pendingLoad: Promise<void> | undefined;
  private pendingLoadId: string | undefined;

  setTranslationFunction(fn: (key: string) => string): void {
    this.translateFn = fn;
  }

  updateFormData<K extends keyof CreatorMessageFormData>(field: K, value: CreatorMessageFormData[K]): void {
    const current = this.state.formData.get();
    this.state.formData.set({ ...current, [field]: value });

    const errors = this.state.validationErrors.get();
    if (errors[field as keyof CreatorMessageFormErrors]) {
      const newErrors = { ...errors };
      delete newErrors[field as keyof CreatorMessageFormErrors];
      this.state.validationErrors.set(newErrors);
    }
  }

  validateForm(): boolean {
    const data = this.state.formData.get();
    const errors: CreatorMessageFormErrors = {};

    if (!data.title.trim()) {
      errors.title = this.translateFn("creatorMessages.errors.titleRequired");
    }
    if (!data.content.trim()) {
      errors.content = this.translateFn("creatorMessages.errors.contentRequired");
    }
    if (data.segments.length === 0) {
      errors.segments = this.translateFn("creatorMessages.errors.segmentsRequired");
    }
    if (data.ctaEnabled) {
      if (!data.ctaText.trim()) {
        errors.ctaText = this.translateFn("creatorMessages.errors.ctaTextRequired");
      }
      if (!data.ctaUrl.trim()) {
        errors.ctaUrl = this.translateFn("creatorMessages.errors.ctaUrlRequired");
      } else if (!/^(https?:\/\/|www\.).+/.test(data.ctaUrl.trim())) {
        errors.ctaUrl = this.translateFn("creatorMessages.errors.ctaUrlInvalid");
      }
    }
    if (data.scheduleEnabled) {
      if (!data.scheduledAt) {
        errors.scheduledAt = this.translateFn("creatorMessages.errors.dateRequired");
      }
      if (!data.scheduledTime) {
        errors.scheduledTime = this.translateFn("creatorMessages.errors.timeRequired");
      }
    }

    this.state.validationErrors.set(errors);
    return Object.keys(errors).length === 0;
  }

  private normalizeUrl(url: string): string {
    const trimmed = url.trim();
    if (trimmed.startsWith("www.")) return `https://${trimmed}`;
    return trimmed;
  }

  private getScheduledAt(data: CreatorMessageFormData): string | null {
    return data.scheduleEnabled && data.scheduledAt && data.scheduledTime
      ? new Date(`${data.scheduledAt}T${data.scheduledTime}`).toISOString()
      : null;
  }

  async saveAsDraft(): Promise<boolean> {
    const data = this.state.formData.get();

    const errors: CreatorMessageFormErrors = {};
    if (!data.title.trim()) {
      errors.title = this.translateFn("creatorMessages.errors.titleRequired");
    }
    if (!data.content.trim()) {
      errors.content = this.translateFn("creatorMessages.errors.contentRequired");
    }
    if (data.segments.length === 0) {
      errors.segments = this.translateFn("creatorMessages.errors.segmentsRequired");
    }
    if (Object.keys(errors).length > 0) {
      this.state.validationErrors.set(errors);
      return false;
    }

    this.state.isSubmitting.set(true);

    try {
      const messageId = this.state.messageId.get();
      const scheduledAt = this.getScheduledAt(data);

      // rpcService.call() throws on failure and returns the unwrapped result directly
      if (messageId) {
        const result = await this.rpcService.call<UpdateCreatorMessageResult>("update-creator-message", {
          id: messageId,
          title: data.title,
          content: data.content,
          segments: data.segments,
          ctaEnabled: data.ctaEnabled,
          ctaText: data.ctaEnabled ? data.ctaText : null,
          ctaUrl: data.ctaEnabled ? this.normalizeUrl(data.ctaUrl) : null,
          scheduledAt,
          status: "draft",
        });
        this.state.messageStatus.set(result.status);
      } else {
        const result = await this.rpcService.call<CreateCreatorMessageResult>("create-creator-message", {
          title: data.title,
          content: data.content,
          segments: data.segments,
          ctaEnabled: data.ctaEnabled,
          ctaText: data.ctaEnabled ? data.ctaText : undefined,
          ctaUrl: data.ctaEnabled ? this.normalizeUrl(data.ctaUrl) : undefined,
          scheduledAt,
        });
        this.state.messageId.set(result.id);
        this.state.messageStatus.set(result.status);
      }
      return true;
    } catch {
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async saveAndSchedule(): Promise<boolean> {
    if (!this.validateForm()) return false;

    const data = this.state.formData.get();
    this.state.isSubmitting.set(true);

    try {
      const messageId = this.state.messageId.get();
      const scheduledAt = this.getScheduledAt(data);

      const payload = {
        title: data.title,
        content: data.content,
        segments: data.segments,
        ctaEnabled: data.ctaEnabled,
        ctaText: data.ctaEnabled ? data.ctaText : null,
        ctaUrl: data.ctaEnabled ? this.normalizeUrl(data.ctaUrl) : null,
        scheduledAt,
      };

      let resolvedMessageId: string;

      if (messageId) {
        const result = await this.rpcService.call<UpdateCreatorMessageResult>("update-creator-message", {
          id: messageId,
          ...payload,
          status: data.scheduleEnabled ? "scheduled" : undefined,
        });
        this.state.messageStatus.set(result.status);
        resolvedMessageId = messageId;
      } else {
        const createResult = await this.rpcService.call<CreateCreatorMessageResult>("create-creator-message", {
          ...payload,
        });
        this.state.messageId.set(createResult.id);
        resolvedMessageId = createResult.id;

        if (data.scheduleEnabled) {
          const scheduleResult = await this.rpcService.call<UpdateCreatorMessageResult>("update-creator-message", {
            id: createResult.id,
            status: "scheduled",
          });
          this.state.messageStatus.set(scheduleResult.status);
        }
      }

      if (!data.scheduleEnabled) {
        await this.rpcService.call<SendCreatorMessageResult>("send-creator-message", {
          messageId: resolvedMessageId,
        });
        this.state.messageStatus.set("sent");
      }

      return true;
    } catch {
      return false;
    } finally {
      this.state.isSubmitting.set(false);
    }
  }

  async loadMessage(id: string): Promise<void> {
    if (this.pendingLoadId === id && this.pendingLoad) {
      return this.pendingLoad;
    }
    this.pendingLoadId = id;
    this.pendingLoad = this.doLoadMessage(id);
    try {
      await this.pendingLoad;
    } finally {
      this.pendingLoad = undefined;
      this.pendingLoadId = undefined;
    }
  }

  private async doLoadMessage(id: string): Promise<void> {
    this.state.isLoading.set(true);
    try {
      const msg = await this.rpcService.call<GetCreatorMessageByIdResult>("get-creator-message-by-id", { id });
      const hasSchedule = !!msg.scheduled_at;
      const scheduledDate = hasSchedule ? msg.scheduled_at!.substring(0, 10) : "";
      const scheduledTime = hasSchedule ? msg.scheduled_at!.substring(11, 16) : "";

      this.state.formData.set({
        title: msg.title,
        content: msg.content,
        segments: msg.segments,
        ctaEnabled: msg.cta_enabled,
        ctaText: msg.cta_text ?? "",
        ctaUrl: msg.cta_url ?? "",
        scheduledAt: scheduledDate,
        scheduledTime: scheduledTime,
        scheduleEnabled: hasSchedule,
      });
      this.state.messageId.set(msg.id);
      this.state.messageStatus.set(msg.status);
    } finally {
      this.state.isLoading.set(false);
    }
  }

  async loadSegments(): Promise<void> {
    if (this.state.segmentsLoading.get()) return;
    this.state.segmentsLoading.set(true);
    try {
      const result = await this.rpcService.call<GetMessageSegmentsResult>("get-message-segments", {});
      this.state.availableSegments.set(result.segments);
    } catch {
      this.state.availableSegments.set([]);
    } finally {
      this.state.segmentsLoading.set(false);
    }
  }

  reset(): void {
    this.state.reset();
  }
}
